import type { ApiEnvelope } from "../types/order";
import { AUTH_UNAUTHORIZED_EVENT, clearAuthStorage, getAccessToken } from "./authStorage";

const apiBase = "/api";

interface RequestJsonOptions extends RequestInit {
  skipAuth?: boolean;
}

export class ApiRequestError extends Error {
  status: number;
  code?: number;

  constructor(message: string, status: number, code?: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

export async function requestJson<T>(path: string, init?: RequestJsonOptions): Promise<T> {
  const headers = new Headers(init?.headers);
  const token = init?.skipAuth ? null : getAccessToken();
  const { skipAuth: _skipAuth, ...requestInit } = init || {};

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (init?.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${apiBase}${path}`, {
    ...requestInit,
    headers
  });

  const contentType = response.headers.get("Content-Type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as ApiEnvelope<T>)
    : null;

  if (!response.ok) {
    if (response.status === 401 && !init?.skipAuth) {
      clearAuthStorage();
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
      if (window.location.pathname !== "/login") {
        const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(`/login?redirect=${encodeURIComponent(redirect)}`);
      }
    }

    throw new ApiRequestError(
      payload?.msg || `请求失败，状态码 ${response.status}`,
      response.status,
      payload?.code
    );
  }

  if (!payload) {
    throw new ApiRequestError("服务端返回了非 JSON 响应", response.status);
  }

  if (payload.code !== 0) {
    throw new ApiRequestError(payload.msg || "请求失败", response.status, payload.code);
  }

  return payload.data;
}
